import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';

import { DialogBody, DialogDescription, DialogHeader, DialogTitle } from '@/components/core/dialog';
import { DatePickerFormField } from '@/components/forms/date-picker-form-field';
import { FormDialogContent } from '@/components/forms/form-dialog';
import { NumberFormField } from '@/components/forms/number-form-field';
import type { Result } from '@/shared/helpers/Result';

import { describeApplicability } from './surcharge-applicability';
import { surchargeChargeMethodTitle, type SurchargeRow } from './surcharge-columns';

export type UpdateSurchargeRateOutput = {
  /** The new rate, already converted to backend units (fraction for percentage). */
  rate: number;
  /** ISO timestamp the new version takes effect from; undefined = now. */
  validFrom?: string;
};

type Props = {
  surcharge: SurchargeRow;
  onSubmit: (data: UpdateSurchargeRateOutput) => Promise<Result> | Promise<void> | void;
};

/**
 * "Update rate" form for a surcharge. Rows are immutable on the backend, so this supersedes: the
 * identity fields (name, scope, method, conditions) are shown read-only and the submit creates a
 * new version with the same identity and the new rate/effective-from. The previous rate remains
 * as audit history.
 */
export function UpdateSurchargeRateDialogContent({ surcharge, onSubmit }: Props) {
  const isPercentage = surcharge.chargeMethod === 'PERCENTAGE';

  const schema = useMemo(
    () =>
      z.object({
        rate: z
          .number('Enter a rate')
          .positive('Enter a rate greater than zero')
          .refine((rate) => !isPercentage || rate <= 100, 'A percentage rate cannot exceed 100'),
        validFrom: z.date().optional(),
      }),
    [isPercentage],
  );

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      // Prefill in human units; toFixed bounds the float noise of fraction × 100.
      rate: isPercentage ? Number((surcharge.rate * 100).toFixed(6)) : surcharge.rate,
      validFrom: undefined,
    },
  });

  return (
    <FormDialogContent
      form={form}
      submitLabel="Update rate"
      onSubmit={({ rate, validFrom }) =>
        onSubmit({
          rate: isPercentage ? rate / 100 : rate,
          ...(validFrom && { validFrom: validFrom.toISOString() }),
        })
      }
    >
      <DialogHeader>
        <DialogTitle>Update rate</DialogTitle>
        <DialogDescription>
          This creates a new version effective from the chosen date; the previous rate is kept for
          audit history.
        </DialogDescription>
      </DialogHeader>
      <DialogBody className="grid gap-4">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
          <dt className="text-muted-foreground">Name</dt>
          <dd>{surcharge.name}</dd>
          <dt className="text-muted-foreground">Applies to</dt>
          <dd>{surcharge.appliesTo}</dd>
          <dt className="text-muted-foreground">Method</dt>
          <dd>{surchargeChargeMethodTitle[surcharge.chargeMethod]}</dd>
          <dt className="text-muted-foreground">Conditions</dt>
          <dd>{surcharge.applicability ? describeApplicability(surcharge.applicability) : '—'}</dd>
        </dl>
        <NumberFormField
          control={form.control}
          name="rate"
          label={isPercentage ? 'Rate (%)' : 'Rate ($)'}
          min={0}
          step="any"
        />
        <DatePickerFormField
          control={form.control}
          name="validFrom"
          label="Effective from"
          description="Defaults to now. Pick a future date to schedule the new rate."
        />
      </DialogBody>
    </FormDialogContent>
  );
}
